import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CaseNoteResponse, ClientDetailResponse, UpdateCaseNoteRequest, caseNotesApi } from "@/api";
import { AppointmentAttendanceStatus } from "@/pages/clients/client/components/tabs/notes-tab/CaseNotes.types";
import { CaseNoteFormValues } from "@/pages/clients/client/components/tabs/notes-tab/components/case-note-form/CaseNoteForm.types";
import { useCaseNoteForm } from "@/pages/clients/client/components/tabs/notes-tab/components/case-note-form/hooks/useCaseNoteForm";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useCaseNoteEdit(client: ClientDetailResponse, caseNote: CaseNoteResponse, onClose: () => void) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();
  const {
    typeOptions,
    locationOptions,
    resultOptions,
    appointmentStatusOptions,
    isConversationType,
    validationSchema,
  } = useCaseNoteForm();

  const updateCaseNoteMutation = useMutation({
    mutationFn: (request: UpdateCaseNoteRequest) =>
      caseNotesApi.updateCaseNote({
        clientId: client.clientId,
        noteId: caseNote.caseNoteId,
        updateCaseNoteRequest: request,
      }),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, client.clientId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientOverview, client.clientId] });
      showSuccess(t("clients.client.caseNotes.editDialog.successfullyUpdated"));
      onClose();
    },
  });

  const formik = useFormikWithDirtyForm<CaseNoteFormValues>({
    initialValues: {
      type: caseNote.type,
      date: caseNote.date,
      time: caseNote.time,
      assignedCounselorId: caseNote.assignedCounselorId,
      location: caseNote.location,
      content: caseNote.content,
      result: caseNote.result,
      appointmentStatus: (caseNote.appointmentStatus as AppointmentAttendanceStatus | null) ?? null,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      const request: UpdateCaseNoteRequest = {
        type: values.type,
        date: values.date,
        time: values.time,
        assignedCounselorId: values.assignedCounselorId,
        location: values.location,
        content: values.content,
        result: values.result,
        appointmentStatus: isConversationType(values.type) ? values.appointmentStatus : null,
      };

      try {
        await updateCaseNoteMutation.mutateAsync(request);
      } catch {
        // Reported by the global error handling; the dialog stays open so the input is kept.
      }
    },
  });

  return { formik, typeOptions, locationOptions, resultOptions, appointmentStatusOptions, isConversationType };
}
